// src/legal/TermsOfService.tsx
// Algemene Voorwaarden voor WFT Study Buddy
// Laatst bijgewerkt: December 2024

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Mail, MapPin, AlertTriangle } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

const TermsOfService: React.FC = () => {
  useSEO({
    title: 'Algemene Voorwaarden | WFT Study Buddy',
    description: 'Algemene Voorwaarden van WFT Study Buddy. Lees de gebruiksvoorwaarden van ons platform.',
    canonicalUrl: '/terms',
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Terug naar home</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-xl border border-white/50 p-8 md:p-12">
          {/* Title */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center">
              <FileText className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Algemene Voorwaarden</h1>
              <p className="text-gray-500 mt-1" suppressHydrationWarning>Laatst bijgewerkt: {new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
          </div>

          {/* Important Notice Box */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-10">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800 mb-2">Belangrijke informatie</h3>
                <p className="text-amber-700 text-sm leading-relaxed">
                  WFT Study Buddy is een <strong>onafhankelijk oefenplatform</strong> en is niet gelieerd aan, 
                  goedgekeurd door, of verbonden met het CDFD, DUO, AFM of enige andere officiële exameninstantie. 
                  Onze oefenvragen zijn bedoeld als studieondersteuning en zijn <strong>geen replica's</strong> van 
                  officiële examenvragen.
                </p>
              </div>
            </div>
          </div>

          {/* Introduction */}
          <section className="mb-10">
            <p className="text-gray-700 leading-relaxed">
              Welkom bij WFT Study Buddy. Door gebruik te maken van onze website en diensten gaat u akkoord 
              met deze Algemene Voorwaarden. Lees deze voorwaarden zorgvuldig door voordat u onze diensten 
              gebruikt. Deze voorwaarden zijn van toepassing op alle bezoekers, gebruikers en anderen die 
              toegang hebben tot de dienst.
            </p>
          </section>

          {/* Section 1: Definities */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center text-sm font-bold">1</span>
              Definities
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>"Platform":</strong> De website wft-study-buddy.nl en alle daaraan gerelateerde diensten, applicaties en content.</li>
                <li><strong>"Gebruiker":</strong> Elke natuurlijke of rechtspersoon die gebruik maakt van het Platform.</li>
                <li><strong>"Diensten":</strong> Alle door WFT Study Buddy aangeboden producten en diensten, waaronder oefenvragen, proefexamens, samenvattingen en leermaterialen.</li>
                <li><strong>"AI-gegenereerde content":</strong> Vragen, uitleg en andere content die (mede) door kunstmatige intelligentie is gecreëerd.</li>
                <li><strong>"Credits":</strong> De virtuele eenheden waarmee toegang tot bepaalde Diensten kan worden verkregen.</li>
              </ul>
            </div>
          </section>

          {/* Section 2: Aard van de Dienst */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center text-sm font-bold">2</span>
              Aard van de Dienst
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p><strong>2.1</strong> WFT Study Buddy is een onafhankelijk online leerplatform dat studenten helpt bij de voorbereiding op WFT-examens (Wet op het financieel toezicht).</p>
              
              <p><strong>2.2</strong> Wij bieden:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>AI-gegenereerde oefenvragen gebaseerd op officiële toetstermen en eindtermen</li>
                <li>Proefexamens in een vergelijkbaar format als het officiële examen</li>
                <li>Samenvattingen en studiemateriaal</li>
                <li>Voortgangsregistratie en statistieken</li>
              </ul>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <p className="text-blue-800 text-sm">
                  <strong>Let op:</strong> Onze dienst is uitsluitend bedoeld als aanvullend studiemateriaal. 
                  Wij vervangen niet de officiële cursussen, lesmateriaal of examens van erkende opleidingsinstituten.
                </p>
              </div>
            </div>
          </section>

          {/* Section 3: Disclaimer & Aansprakelijkheid */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center text-sm font-bold">3</span>
              Disclaimer en Aansprakelijkheidsbeperking
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p><strong>3.1 Geen garantie op slagen</strong></p>
              <p className="ml-4">
                WFT Study Buddy garandeert op geen enkele wijze dat het gebruik van onze diensten zal leiden 
                tot het behalen van een WFT-examen. Examenresultaten zijn afhankelijk van vele factoren, 
                waaronder maar niet beperkt tot: persoonlijke inzet, voorkennis, officieel lesmateriaal 
                en de specifieke examenvragen op de dag van het examen.
              </p>

              <p><strong>3.2 Vraagkwaliteit en -representativiteit</strong></p>
              <p className="ml-4">
                Hoewel wij streven naar hoge kwaliteit en relevantie van onze oefenvragen, kunnen wij niet 
                garanderen dat:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-8">
                <li>Onze vragen exact overeenkomen met officiële examenvragen</li>
                <li>De moeilijkheidsgraad exact overeenkomt met het officiële examen</li>
                <li>Alle informatie in onze vragen en uitleg 100% actueel of correct is</li>
                <li>AI-gegenereerde content vrij is van fouten of onnauwkeurigheden</li>
              </ul>

              <p><strong>3.3 Actualiteit van informatie</strong></p>
              <p className="ml-4">
                WFT-wetgeving en exameneisen worden periodiek aangepast, met name per 1 april van elk jaar. 
                Hoewel wij ons inspannen om onze content actueel te houden, kan er vertraging optreden. 
                Raadpleeg altijd de officiële bronnen (CDFD, DUO) voor de meest actuele exameneisen.
              </p>

              <p><strong>3.4 Beperking van aansprakelijkheid</strong></p>
              <div className="bg-gray-100 rounded-lg p-4 ml-4">
                <p className="text-gray-800">
                  Voor zover wettelijk toegestaan, is WFT Study Buddy niet aansprakelijk voor:
                </p>
                <ul className="list-disc list-inside space-y-2 mt-2 text-gray-700">
                  <li>Directe of indirecte schade voortvloeiend uit het gebruik van onze diensten</li>
                  <li>Het niet behalen van een examen na gebruik van onze diensten</li>
                  <li>Eventuele onjuistheden in vragen, antwoorden of uitleg</li>
                  <li>Tijdelijke onbeschikbaarheid van het Platform</li>
                  <li>Beslissingen genomen op basis van informatie op het Platform</li>
                </ul>
              </div>

              <p><strong>3.5 Maximale aansprakelijkheid</strong></p>
              <p className="ml-4">
                Indien WFT Study Buddy toch aansprakelijk wordt gehouden, is deze aansprakelijkheid beperkt 
                tot maximaal het bedrag dat de Gebruiker in de 12 maanden voorafgaand aan de schadeveroorzakende 
                gebeurtenis aan WFT Study Buddy heeft betaald, met een maximum van €100,-.
              </p>
            </div>
          </section>

          {/* Section 4: Gebruik van AI */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center text-sm font-bold">4</span>
              Gebruik van Kunstmatige Intelligentie
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p><strong>4.1</strong> Een deel van onze content, waaronder oefenvragen en uitleg, wordt gegenereerd met behulp van kunstmatige intelligentie (AI).</p>
              
              <p><strong>4.2</strong> AI-gegenereerde content wordt waar mogelijk gecontroleerd, maar kan fouten bevatten. Gebruikers worden aangemoedigd om:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Antwoorden kritisch te beoordelen</li>
                <li>Bij twijfel officiële bronnen te raadplegen</li>
                <li>Mogelijke fouten te melden via onze contactpagina</li>
              </ul>

              <p><strong>4.3</strong> Wij verbeteren continu onze AI-modellen en validatieprocessen om de kwaliteit te waarborgen.</p>
            </div>
          </section>

          {/* Section 5: Account en Registratie */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center text-sm font-bold">5</span>
              Account en Registratie
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p><strong>5.1</strong> Voor het gebruik van bepaalde diensten is registratie vereist. U bent verantwoordelijk voor het geheimhouden van uw inloggegevens.</p>
              
              <p><strong>5.2</strong> U garandeert dat alle verstrekte informatie juist en volledig is.</p>
              
              <p><strong>5.3</strong> Eén account is strikt persoonlijk en mag niet worden gedeeld met derden.</p>
              
              <p><strong>5.4</strong> Wij behouden het recht om accounts te beëindigen bij misbruik of schending van deze voorwaarden.</p>
            </div>
          </section>

          {/* Section 6: Betalingen en Credits */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center text-sm font-bold">6</span>
              Betalingen en Credits
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p><strong>6.1</strong> Betalingen worden verwerkt via Stripe, een beveiligde betalingsprovider. Wij slaan geen creditcardgegevens op.</p>
              
              <p><strong>6.2</strong> Prijzen zijn inclusief BTW, tenzij anders vermeld.</p>
              
              <p><strong>6.3</strong> Gekochte credits of abonnementen zijn niet overdraagbaar en niet restitueerbaar, tenzij anders bepaald door toepasselijk consumentenrecht.</p>
              
              <p><strong>6.4</strong> Credits hebben geen vervaldatum, maar kunnen vervallen bij accountbeëindiging.</p>

              <p><strong>6.5 Herroepingsrecht</strong></p>
              <p className="ml-4">
                Op grond van de Wet Koop op Afstand heeft u bij digitale producten het recht om binnen 14 dagen 
                na aankoop zonder opgaaf van reden de overeenkomst te ontbinden, mits u nog geen gebruik heeft 
                gemaakt van de dienst. Door een proefexamen te starten of credits te gebruiken, stemt u in met 
                het afzien van uw herroepingsrecht voor dat specifieke product.
              </p>
            </div>
          </section>

          {/* Section 7: Intellectueel Eigendom */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center text-sm font-bold">7</span>
              Intellectueel Eigendom
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p><strong>7.1</strong> Alle content op het Platform, inclusief maar niet beperkt tot teksten, vragen, afbeeldingen, logo's en software, is eigendom van WFT Study Buddy of haar licentiegevers.</p>
              
              <p><strong>7.2</strong> Het is niet toegestaan om content te kopiëren, verspreiden, publiceren of commercieel te exploiteren zonder schriftelijke toestemming.</p>
              
              <p><strong>7.3</strong> Officiële WFT-toetstermen en eindtermen zijn afkomstig van het CDFD en worden gebruikt voor educatieve doeleinden.</p>
            </div>
          </section>

          {/* Section 8: Gedragsregels */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center text-sm font-bold">8</span>
              Gedragsregels
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>Bij gebruik van het Platform is het niet toegestaan om:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Het Platform te gebruiken voor onwettige doeleinden</li>
                <li>Beveiligingsmaatregelen te omzeilen of te proberen te omzeilen</li>
                <li>Content te delen met niet-geregistreerde gebruikers</li>
                <li>Geautomatiseerde systemen te gebruiken om content te verzamelen (scraping)</li>
                <li>De werking van het Platform te verstoren</li>
              </ul>
            </div>
          </section>

          {/* Section 9: Wijzigingen */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center text-sm font-bold">9</span>
              Wijzigingen
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p><strong>9.1</strong> Wij behouden het recht om deze Algemene Voorwaarden te wijzigen. Wijzigingen worden op deze pagina gepubliceerd.</p>
              
              <p><strong>9.2</strong> Bij materiële wijzigingen informeren wij geregistreerde gebruikers via e-mail.</p>
              
              <p><strong>9.3</strong> Voortgezet gebruik van het Platform na wijzigingen geldt als acceptatie van de gewijzigde voorwaarden.</p>
            </div>
          </section>

          {/* Section 10: Toepasselijk Recht */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center text-sm font-bold">10</span>
              Toepasselijk Recht en Geschillen
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p><strong>10.1</strong> Op deze Algemene Voorwaarden is Nederlands recht van toepassing.</p>
              
              <p><strong>10.2</strong> Geschillen worden bij voorkeur in onderling overleg opgelost. Indien dit niet mogelijk is, worden geschillen voorgelegd aan de bevoegde rechter in Nederland.</p>
              
              <p><strong>10.3</strong> Als consument kunt u ook een klacht indienen via het Europees platform voor online geschillenbeslechting (ODR): <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">ec.europa.eu/consumers/odr</a></p>
            </div>
          </section>

          {/* Section 11: Contact */}
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center text-sm font-bold">11</span>
              Contact
            </h2>
            <div className="bg-gray-50 rounded-xl p-6 space-y-4">
              <p className="text-gray-700 font-medium">WFT Study Buddy</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-gray-600">
                  <Mail className="w-5 h-5 text-purple-500" />
                  <a href="mailto:info@wft-study-buddy.nl" className="hover:text-purple-600">
                    info@wft-study-buddy.nl
                  </a>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <MapPin className="w-5 h-5 text-purple-500" />
                  <span>Nederland</span>
                </div>
              </div>
              <p className="text-gray-600 text-sm mt-4">
                Voor vragen over deze Algemene Voorwaarden kunt u contact met ons opnemen via bovenstaande gegevens.
              </p>
            </div>
          </section>

          {/* Related Links */}
          <section className="border-t border-gray-200 pt-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Gerelateerde documenten</h3>
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/privacy" 
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
              >
                Privacybeleid
              </Link>
              <Link 
                to="/cookie-policy" 
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
              >
                Cookiebeleid
              </Link>
            </div>
          </section>
        </div>

        {/* Back to top */}
        <div className="text-center mt-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-white/80 backdrop-blur-sm rounded-xl text-gray-700 hover:bg-white hover:shadow-lg transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
            Terug naar home
          </Link>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;